import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Button } from 'antd';
import { SaveOutlined, MinusCircleOutlined, PlusOutlined, ExpandOutlined, CompressOutlined, LinkOutlined } from '@ant-design/icons';
import { FlowNode, NODE_STYLES } from '../types/flow';
import { EXECUTOR_API } from '../types';

const { TextArea } = Input;
const { Option } = Select;

interface NodeConfigDrawerProps {
  node: FlowNode | null;
  open: boolean;
  onClose: () => void;
  onSave: (nodeId: string, updates: Partial<FlowNode>) => void;
}

interface ConnectorOption { id: string; type: string; name: string; }

const TYPE_LABELS: Record<string, string> = { monday: 'Monday.com', gmail: 'Gmail', gdrive: 'Google Drive' };

type HeaderRow = { key: string; value: string };

// `config.headers` is persisted as a dict ({ "X-Tenant": "acme" }) but the
// Form.List editor works with an array of rows — convert between the two.
function headersToList(headers: unknown): HeaderRow[] {
  if (!headers || typeof headers !== 'object' || Array.isArray(headers)) return [];
  return Object.entries(headers as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: value == null ? '' : String(value),
  }));
}

function listToHeaders(list: HeaderRow[] | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  (list || []).forEach((row) => {
    const k = (row?.key || '').trim();
    if (k) out[k] = row?.value ?? '';
  });
  return out;
}

const NodeConfigDrawer: React.FC<NodeConfigDrawerProps> = ({
  node,
  open,
  onClose,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectors, setConnectors] = useState<ConnectorOption[]>([]);
  const authMode = Form.useWatch('auth_mode', form);

  const fetchConnectors = useCallback(async () => {
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors`);
      if (res.ok) {
        const data = await res.json();
        setConnectors((data.connectors || []).map((c: { id: string; type: string; name: string }) => ({
          id: c.id, type: c.type, name: c.name,
        })));
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (open && node?.type === 'llm') fetchConnectors(); }, [open, node?.type, fetchConnectors]);

  useEffect(() => {
    if (!open) {
      setIsFullscreen(false);
    }
    if (node && open) {
      form.setFieldsValue({
        label: node.label,
        subtitle: node.subtitle || '',
        model: (node.config.model as string) || 'gemini-2.5-flash',
        prompt: (node.config.prompt as string) || (node.type === 'classification' ? 'Debes clasificar el contenido de la reunión' : ''),
        tools: Array.isArray(node.config.tools) ? node.config.tools as string[] : [],
        connector_ids: Array.isArray(node.config.connector_ids) ? node.config.connector_ids as string[] : [],
        output_schema: Array.isArray(node.config.output_schema)
          ? node.config.output_schema
          : [],
        field: (node.config.field as string) || '',
        operator: (node.config.operator as string) || 'equals',
        value: (node.config.value as string) || '',
        true_label: (node.config.true_label as string) || '',
        false_label: (node.config.false_label as string) || '',
        message_text: (node.config.message_text as string) || '',
        classification_target: (node.config.classification_target as string) || '',
        categories: Array.isArray(node.config.categories)
          ? node.config.categories
          : [],
        // http_request fields
        method: (node.config.method as string) || 'POST',
        url: (node.config.url as string) || '',
        auth_mode: (node.config.auth_mode as string) || 'none',
        auth_value: (node.config.auth_value as string) || '',
        auth_header: (node.config.auth_header as string) || 'X-API-Key',
        body_template: (node.config.body_template as string) || '',
        timeout: (node.config.timeout as number) || 30,
        headers_list: headersToList(node.config.headers),
      });
    }
  }, [node, open, form]);

  if (!node) return null;

  const style = NODE_STYLES[node.type];

  const handleFinish = (values: Record<string, unknown>) => {
    const updates: Partial<FlowNode> = {
      label: values.label as string,
      subtitle: (values.subtitle as string) || undefined,
    };

    const config: Record<string, unknown> = { ...node.config };

    switch (node.type) {
      case 'llm':
        config.model = values.model as string;
        config.prompt = values.prompt as string;
        config.tools = (values.tools as string[]) || [];
        config.connector_ids = (values.connector_ids as string[]) || [];
        updates.subtitle = values.model as string;
        break;
      case 'condition':
        config.field = values.field as string;
        config.operator = values.operator as string;
        config.value = values.value as string;
        config.true_label = values.true_label as string;
        config.false_label = values.false_label as string;
        updates.subtitle = `${values.field as string} ${values.operator as string} ${values.value as string}`;
        break;
      case 'human_input':
        config.message_text = values.message_text as string;
        break;
      case 'extraction': {
        const fields = (values.output_schema as { field: string; description: string }[]) || [];
        config.prompt = values.prompt as string;
        config.output_schema = fields;
        updates.subtitle = fields.map((f) => f.field).filter(Boolean).join(', ') || undefined;
        break;
      }
      case 'classification': {
        const cats = (values.categories as { name: string; description: string }[]) || [];
        config.classification_target = values.classification_target as string;
        config.categories = cats;
        updates.subtitle = cats.map((c) => c.name).filter(Boolean).join(', ') || undefined;
        break;
      }
      case 'bigquery_save':
        updates.subtitle = 'flow_executions';
        break;
      case 'http_request': {
        const method = (values.method as string) || 'POST';
        const url = (values.url as string) || '';
        config.method = method;
        config.url = url;
        config.headers = listToHeaders(values.headers_list as HeaderRow[]);
        const authMode = (values.auth_mode as string) || 'none';
        config.auth_mode = authMode;
        // Only persist auth fields relevant to the selected mode. AntD keeps
        // hidden field values (preserve: true), so without this a token entered
        // for bearer/api_key_header would linger in the JSONB after switching
        // back to "none" — a secret left behind for no reason.
        if (authMode === 'bearer' || authMode === 'api_key_header') {
          config.auth_value = (values.auth_value as string) || '';
        } else {
          delete config.auth_value;
        }
        if (authMode === 'api_key_header') {
          config.auth_header = (values.auth_header as string) || 'X-API-Key';
        } else {
          delete config.auth_header;
        }
        config.body_template = (values.body_template as string) || '';
        config.timeout = (values.timeout as number) || 30;
        // Inline config supersedes the legacy connector reference.
        delete config.connector_id;
        updates.subtitle = url ? `${method} ${url}`.slice(0, 60) : method;
        break;
      }
      default:
        break;
    }

    updates.config = config;
    onSave(node.id, updates);
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: style.color,
                flexShrink: 0,
              }}
            />
            Configurar {style.tagLabel}: {node.label}
          </span>
          <Button
            type="text"
            size="small"
            icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setIsFullscreen((v) => !v)}
            title={isFullscreen ? 'Reducir' : 'Pantalla completa'}
            style={{ marginLeft: 12, color: '#8c8c8c' }}
          />
        </div>
      }
      placement="right"
      width={isFullscreen ? '100vw' : 600}
      onClose={onClose}
      open={open}
      destroyOnClose
      styles={{
        mask: {
          backdropFilter: 'blur(6px)',
          background: 'rgba(0, 0, 0, 0.35)',
        },
        body: { paddingBottom: 80, overflowY: 'auto' },
      }}
      className="node-config-drawer"
    >
      <div style={isFullscreen ? { maxWidth: 860, margin: '0 auto' } : undefined}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {/* Common fields */}
        <Form.Item
          label="Nombre del nodo"
          name="label"
          rules={[{ required: true, message: 'Introduce un nombre' }]}
        >
          <Input placeholder="Nombre del nodo" />
        </Form.Item>

        {/* LLM-specific fields */}
        {node.type === 'llm' && (
          <>
            <Form.Item
              label="Modelo"
              name="model"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="gemini-2.5-flash">gemini-2.5-flash</Option>
                <Option value="gemini-2.5-pro">gemini-2.5-pro</Option>
                <Option value="claude-sonnet-4-20250514">claude-sonnet-4</Option>
                <Option value="gpt-4o">gpt-4o</Option>
                <Option value="gpt-4o-mini">gpt-4o-mini</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Prompt" name="prompt">
              <TextArea
                rows={4}
                placeholder="Instrucciones para el LLM en este nodo..."
              />
            </Form.Item>

            <Form.Item
              label="Tools del Marketplace"
              name="tools"
              help="El LLM podrá llamar a estos agentes cuando lo necesite"
            >
              <Select
                mode="multiple"
                placeholder="Selecciona agentes como tools (opcional)..."
                options={[
                  { value: 'lead_enrichment', label: 'Lead Enrichment — Busca leads en el CRM' },
                  { value: 'meeting_doubts', label: 'Meeting Doubts — Extrae dudas de reuniones' },
                ]}
              />
            </Form.Item>

            <Form.Item
              label={<span><LinkOutlined style={{ marginRight: 6, color: '#1677ff' }} />Conectores</span>}
              name="connector_ids"
              help="El LLM tendrá acceso a las herramientas de estos conectores"
            >
              <Select
                mode="multiple"
                placeholder={connectors.length === 0 ? 'Sin conectores configurados — ve a Conectores para añadir' : 'Selecciona conectores...'}
                disabled={connectors.length === 0}
                options={connectors.map(c => ({
                  value: c.id,
                  label: `${TYPE_LABELS[c.type] || c.type} — ${c.name}`,
                }))}
              />
            </Form.Item>
          </>
        )}

        {/* Condition-specific fields */}
        {node.type === 'condition' && (
          <>
            <Form.Item
              label="Campo a evaluar"
              name="field"
              rules={[{ required: true, message: 'Introduce el campo' }]}
            >
              <Input placeholder="intention" />
            </Form.Item>

            <Form.Item
              label="Operador"
              name="operator"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="equals">equals</Option>
                <Option value="not_equals">not equals</Option>
                <Option value="contains">contains</Option>
                <Option value="gt">greater than</Option>
                <Option value="lt">less than</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Valor"
              name="value"
              rules={[{ required: true, message: 'Introduce el valor' }]}
            >
              <Input placeholder="queja" />
            </Form.Item>

            <Form.Item label="Etiqueta rama verdadera" name="true_label">
              <Input placeholder="Es queja" />
            </Form.Item>

            <Form.Item label="Etiqueta rama falsa" name="false_label">
              <Input placeholder="Otra intencion" />
            </Form.Item>
          </>
        )}

        {/* Human Input-specific fields */}
        {node.type === 'human_input' && (
          <Form.Item label="Mensaje / Instrucciones" name="message_text">
            <TextArea
              rows={3}
              placeholder="Instrucciones para el operador humano..."
            />
          </Form.Item>
        )}

        {/* Extraction-specific fields */}
        {node.type === 'extraction' && (
          <>
            <Form.Item label="Instrucciones de extraccion" name="prompt">
              <TextArea
                rows={4}
                placeholder="Extrae los datos estructurados del texto..."
              />
            </Form.Item>

            <Form.List name="output_schema">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: 12, fontWeight: 500, fontSize: 13, color: '#1a1a2e' }}>
                    Campos a extraer
                  </div>
                  {fields.map(({ key, name }) => (
                    <div key={key} className="extraction-field-row">
                      <Form.Item
                        name={[name, 'field']}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[{ required: true, message: 'Campo requerido' }]}
                      >
                        <Input placeholder="field name" size="small" />
                      </Form.Item>
                      <Form.Item
                        name={[name, 'description']}
                        style={{ flex: 2, marginBottom: 0 }}
                      >
                        <Input placeholder="description (optional)" size="small" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        title="Eliminar campo"
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => add({ field: '', description: '' })}
                    style={{ marginTop: 12 }}
                  >
                    + Añadir campo
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}

        {/* BigQuery Save-specific fields */}
        {node.type === 'bigquery_save' && (
          <div style={{ padding: '12px 16px', background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f', fontSize: 13, color: '#389e0d', marginBottom: 16 }}>
            💾 Guarda automáticamente <strong>todos</strong> los outputs de los nodos anteriores en BigQuery.<br />
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
              Tabla: <strong>flow_executions_test</strong> (dev) · <strong>flow_executions</strong> (producción)
            </span>
          </div>
        )}

        {/* Classification-specific fields */}
        {node.type === 'classification' && (
          <>
            <Form.Item
              label="¿Qué quieres clasificar?"
              name="classification_target"
              rules={[{ required: true, message: 'Describe qué quieres clasificar' }]}
            >
              <TextArea
                rows={3}
                maxLength={300}
                showCount
                placeholder="Ej: reuniones de ventas según la fase del proceso comercial en la que se encuentre el cliente"
              />
            </Form.Item>

            <div style={{ marginBottom: 12, fontWeight: 500, fontSize: 13, color: '#1a1a2e' }}>
              Categorías
            </div>

            <Form.List name="categories">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <div
                      key={key}
                      style={{
                        marginBottom: 12,
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: '12px 12px 4px',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <Form.Item
                          name={[name, 'name']}
                          style={{ flex: 1, marginBottom: 8 }}
                          rules={[{ required: true, message: 'Nombre de categoría requerido' }]}
                        >
                          <Input placeholder="presupuesto_firme" />
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          style={{ marginTop: 4 }}
                          title="Eliminar categoría"
                        />
                      </div>
                      <Form.Item
                        name={[name, 'description']}
                        style={{ marginBottom: 8 }}
                      >
                        <TextArea
                          rows={2}
                          placeholder="¿Cómo identificar esta categoría? Ej: el cliente ha confirmado que tiene presupuesto aprobado y quiere avanzar..."
                        />
                      </Form.Item>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => add({ name: '', description: '' })}
                    style={{ marginTop: 4 }}
                  >
                    + Añadir categoría
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}

        {/* HTTP Request-specific fields */}
        {node.type === 'http_request' && (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <Form.Item label="Método" name="method" style={{ width: 120 }} rules={[{ required: true }]}>
                <Select>
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="PATCH">PATCH</Option>
                  <Option value="DELETE">DELETE</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="URL"
                name="url"
                style={{ flex: 1 }}
                rules={[{ required: true, message: 'Introduce la URL' }]}
                help="Soporta {{path.to.value}} del state. Los query params van en la URL."
              >
                <Input placeholder="https://api.example.com/clients/{{lead.clientId}}/budgets" />
              </Form.Item>
            </div>

            <div style={{ marginBottom: 12, fontWeight: 500, fontSize: 13, color: '#1a1a2e' }}>
              Headers
            </div>
            <Form.List name="headers_list">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }) => (
                    <div key={key} className="extraction-field-row">
                      <Form.Item
                        name={[name, 'key']}
                        style={{ flex: 1, marginBottom: 0 }}
                        rules={[{ required: true, message: 'Nombre del header' }]}
                      >
                        <Input placeholder="X-API-Key" size="small" />
                      </Form.Item>
                      <Form.Item name={[name, 'value']} style={{ flex: 2, marginBottom: 0 }}>
                        <Input placeholder="valor (soporta {{...}})" size="small" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        title="Eliminar header"
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => add({ key: '', value: '' })}
                    style={{ marginTop: 12, marginBottom: 8 }}
                  >
                    + Añadir header
                  </Button>
                </>
              )}
            </Form.List>

            <Form.Item label="Autenticación" name="auth_mode" rules={[{ required: true }]}>
              <Select>
                <Option value="none">Sin autenticación</Option>
                <Option value="bearer">Bearer token (Authorization)</Option>
                <Option value="api_key_header">API key en header</Option>
              </Select>
            </Form.Item>

            {authMode === 'api_key_header' && (
              <Form.Item label="Nombre del header" name="auth_header" rules={[{ required: true }]}>
                <Input placeholder="X-API-Key" />
              </Form.Item>
            )}

            {(authMode === 'bearer' || authMode === 'api_key_header') && (
              <Form.Item
                label="Valor del token / API key"
                name="auth_value"
                help="Soporta {{...}} — ej: {{node_outputs.login.body.accessToken}}. Para secretos estáticos reutilizables, considera un Conector."
              >
                <Input placeholder="token, API key o {{...}}" />
              </Form.Item>
            )}

            <Form.Item
              label="Body (JSON)"
              name="body_template"
              help={'Soporta {{path.to.value}}. Ej: {"clientId": {{lead.clientId}}}. Se ignora en GET.'}
            >
              <TextArea rows={4} placeholder={'{"clientId": {{lead.clientId}}, "data": {{budget}}}'} />
            </Form.Item>

            <Form.Item label="Timeout (segundos)" name="timeout">
              <InputNumber min={1} max={300} style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            block
            size="large"
          >
            Guardar Configuracion
          </Button>
        </Form.Item>
      </Form>
      </div>
    </Drawer>
  );
};

export default NodeConfigDrawer;
